import type { FormControlProps } from '@pega/cosmos-react-core';

interface Value {
  name: string;
  email: string;
  locations: string;
  meetings: string;
  meals: string;
  notes: string;
}

interface Field extends FormControlProps {
  help?: FormControlProps['info'];
  validator?: (this: Field) => string;
}

// Value sets used by the mock AI
export const AIValues: Value[] = [
  {
    name: 'Cindy Turner',
    email: 'cindy.turner@pega.com',
    locations: 'Waltham',
    meetings: 'No',
    meals: 'Pizza',
    notes: 'If I could have a window seat, that would be much appreciated!'
  },
  {
    name: 'Seth DeAngelo',
    email: 'seth.deangelo@pega.com',
    locations: 'Waltham,Cambridge',
    meetings: 'Yes',
    meals: 'Salad',
    notes:
      "I selected salad for my meal, but it that is not available, I'm okay with any sandwich you have."
  },
  {
    name: 'Janet Moore',
    email: 'janet.moore@pega.com',
    locations: 'Krakow',
    meetings: 'No',
    meals: 'Sandwich',
    notes: "I'm allergic to nuts, so please don't add any peanut butter to my sandwich."
  },
  {
    name: 'Dean Thomas',
    email: 'dean.thomas@pega.com',
    locations: 'Hyderabad',
    meetings: 'Yes',
    meals: 'Pizza',
    notes: 'I would like to arrange my hours to avoid rush hour.'
  },
  {
    name: 'Kate Mirin',
    email: 'kate.mirin@pega.com',
    locations: 'Waltham,Krakow,Bangalore',
    meetings: 'Yes',
    meals: 'Salad',
    notes: "I'm not too picky!"
  }
];

// Get initial field properties
export const initFields = (): Field[] => {
  const common = { value: undefined, status: undefined };
  return [
    {
      ...common,
      name: 'name',
      label: 'Name',
      required: true,
      validator() {
        if (!this.value) return 'Please enter a first and last name';
        return /^[a-z]+\s[a-z]+$/i.test(this.value)
          ? ''
          : 'You need to enter a first and last name';
      }
    },
    {
      ...common,
      name: 'email',
      label: 'Email',
      required: true,
      validator() {
        if (!this.value) return 'Please enter an email address';
        return /^\S+@pega\.com$/.test(this.value) ? '' : 'Please enter an @pega.com address';
      }
    },
    {
      ...common,
      name: 'locations',
      label: 'Which office(s) have you worked in?',
      required: true,
      validator() {
        if (!this.value) return 'Select at least one location';
        return '';
      }
    },
    {
      ...common,
      name: 'meetings',
      label: 'Do you enjoy early morning meetings?'
    },
    {
      ...common,
      name: 'meals',
      label: 'What would you like to eat?',
      help: 'Selections will be made on a first-come basis'
    },
    {
      ...common,
      name: 'notes',
      label: 'Special requests',
      help: 'We will take any of your requests into consideration'
    }
  ];
};

// Handles setting of state for all fields
export const fieldReducer = (
  fields: Field[],
  {
    type,
    name,
    value
  }: {
    type: 'blur' | 'change' | 'submit' | 'cancel' | 'ai';
    name?: string;
    value?: string;
  }
) => {
  return fields.map(field => {
    if (field.name === name) {
      if (name === 'locations' && field.value && value && type === 'change') {
        const selectedValues = field.value.split(',');
        const indexOfValue = selectedValues.findIndex(i => i === value);
        if (indexOfValue !== -1) {
          selectedValues.splice(indexOfValue, 1);
        } else {
          selectedValues.push(value);
        }
        field.value = selectedValues.join(',');
      } else {
        field.value = value;
      }

      if (type === 'cancel') {
        field.status = undefined;
        field.info = field.help;
      } else if (type === 'blur' || type === 'submit' || field.status === 'error') {
        const error = field.validator?.() || '';
        if ((field.required || field.value) && error) {
          field.status = 'error';
          field.info = error;
        } else {
          field.status = undefined;
          field.info = field.help;
        }
      }
    }
    return field;
  });
};
